import { useEffect, useState } from "react";
import { Loader2, Building2, Plus, Trash2, Edit2, Save, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";
import { Label } from "../../../../ui/label";
import { Switch } from "../../../../ui/switch";
import { supabaseReservation } from "../../../../../integrations/supabase/client";
import { useToast } from "../../../../..//hooks/use-toast";
import { TimeSlotManager } from "./TimeSlotManager";
import { useAuth } from "../../../../../contexts/AuthContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../../ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import axios from "axios";
import { apiUrl, clientheaderconfig } from "../../../../Helpers/Config";

interface Outlet {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  hours: string | null;
  is_active: boolean;
}

interface NewOutlet {
  name: string;
  address: string;
  phone: string;
  email: string;
  hours: string;
}
interface OutletProps {
  outletList?:any[];
  onRecallOulet: (recall: boolean) => void;
  showAddOutlet:boolean;
}

export const OutletManager = ({ outletList, onRecallOulet, showAddOutlet}: OutletProps) => {
  const { toast } = useToast();
  const { accountId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newOutlet, setNewOutlet] = useState<NewOutlet>({
    name: "",
    address: "",
    phone: "",
    email: "",
    hours: "Monday-Sunday: 5:00 PM - 10:00 PM",
  });
  const [editForm, setEditForm] = useState<Outlet | null>(null);

  useEffect(() => {
    fetchOutlets();
  }, []);

  useEffect(() => {
  const outletName = newOutlet?.name || '';
  if (!outletName) return;

  const foundItem = outletList.find(
    (item) => item._id === outletName
  );

  setNewOutlet((prev) => ({
    ...prev,
    address: foundItem?.outletAddress || '',
    phone: foundItem?.outletPhone || '',
  }));

}, [newOutlet.name]);


  const fetchOutlets = async () => {
    const { data, error } = await supabaseReservation
      .from("outlets")
      .select("*")
      .order("name");

    if (error) {
      console.error("Error fetching outlets:", error);
      toast({
        title: "Error",
        description: "Failed to load outlets",
        variant: "destructive",
      });
    } else {
      setOutlets(data || []);
    }
    setLoading(false);
  };

  const handleAddOutlet = async () => {
    if (!newOutlet.name.trim()) {
      toast({
        title: "Error",
        description: "Outlet name is required",
        variant: "destructive",
      });
      return;
    }

    if (!accountId) {
      toast({
        title: "Error",
        description: "Account not found. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

   const foundItem = outletList.find(
    (item) => item._id === newOutlet.name
  );

    const { data, error } = await supabaseReservation
      .from("outlets")
      .insert({
        name: foundItem.label.trim(),
        address: newOutlet.address.trim() || null,
        phone: newOutlet.phone.trim() || null,
        email: newOutlet.email.trim() || null,
        hours: newOutlet.hours.trim() || null,
        is_active: true,
        account_id: accountId,
      })
      .select()
      .single();

    if (error) {
      console.error("Error adding outlet:", error);
      toast({
        title: "Error",
        description: "Failed to add outlet",
        variant: "destructive",
      });
    } else {
      updatesupabase(newOutlet.name, data.id)
      toast({
        title: "Outlet Added",
        description: `${newOutlet.name} has been created`,
      });
      setOutlets([...outlets, data]);
      setShowAddForm(false);
      setNewOutlet({
        name: "",
        address: "",
        phone: "",
        email: "",
        hours: "Monday-Sunday: 5:00 PM - 10:00 PM",
      });
    }
    setSaving(false);
  };

  const updatesupabase = (outletID:string, supabaseID:any) => {
        const postObject = {
          outletID,
          supabaseID,
          supabaseIDType:'reservation'
        };
        axios
          .post(apiUrl + "outlet/updatesupabase", postObject, clientheaderconfig)
          .then((res) => {
            console.log(res.data, "res.datares.datares.data");
            onRecallOulet(true)
          })
          .catch((e) => {
            console.log(e);
          });
    }

  const handleUpdateOutlet = async () => {
    if (!editForm) return;

    setSaving(true);
    const { error } = await supabaseReservation
      .from("outlets")
      .update({
        name: editForm.name.trim(),
        address: editForm.address?.trim() || null,
        phone: editForm.phone?.trim() || null,
        email: editForm.email?.trim() || null,
        hours: editForm.hours?.trim() || null,
        is_active: editForm.is_active,
      })
      .eq("id", editForm.id);

    if (error) {
      console.error("Error updating outlet:", error);
      toast({
        title: "Error",
        description: "Failed to update outlet",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Outlet Updated",
        description: `${editForm.name} has been updated`,
      });
      setOutlets(outlets.map(o => o.id === editForm.id ? editForm : o));
      setEditingId(null);
      setEditForm(null);
    }
    setSaving(false);
  };

  const handleDeleteOutlet = async (id: string, name: string) => {
    /* if (!confirm(`Are you sure you want to delete "${name}"? This will also delete all associated time slots.`)) {
      return;
    } */

    const { error } = await supabaseReservation
      .from("outlets")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting outlet:", error);
      toast({
        title: "Error",
        description: "Failed to delete outlet",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Outlet Deleted",
        description: `${name} has been removed`,
      });
      setOutlets(outlets.filter(o => o.id !== id));
      if (expandedId === id) setExpandedId(null);
    }
  };

  const handleToggleActive = async (outlet: Outlet) => {
    const { error } = await supabaseReservation
      .from("outlets")
      .update({ is_active: !outlet.is_active })
      .eq("id", outlet.id);

    if (error) {
      console.error("Error toggling outlet status:", error);
    } else {
      setOutlets(outlets.map(o => 
        o.id === outlet.id ? { ...o, is_active: !o.is_active } : o
      ));
    }
  };

  const startEditing = (outlet: Outlet) => {
    setEditingId(outlet.id);
    setEditForm({ ...outlet });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {outlets.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4 bg-background rounded-lg border border-border">
          No outlets configured
        </p>
      ) : (
        outlets.map((outlet) => (
          <Collapsible
            key={outlet.id}
            open={expandedId === outlet.id}
            onOpenChange={(open) => setExpandedId(open ? outlet.id : null)}
          >
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              {editingId === outlet.id && editForm ? (
                // Edit Mode
                <div className="p-4 space-y-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="bg-background"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={editForm.phone || ""}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editForm.email || ""}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="bg-background"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Textarea
                      value={editForm.address || ""}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      rows={2}
                      className="bg-background resize-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hours</Label>
                    <Textarea
                      value={editForm.hours || ""}
                      onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })}
                      rows={2}
                      className="bg-background resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editForm.is_active}
                      onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                    />
                    <Label>Active</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleUpdateOutlet} disabled={saving} className="flex-1">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save
                    </Button>
                    <Button variant="outline" onClick={cancelEditing}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                  <div className="pt-4 border-t border-border">
                    <h5 className="font-medium text-foreground mb-4">Time Slots for {editForm.name}</h5>
                    <TimeSlotManager outletId={outlet.id} />
                  </div>
                </div>
              ) : (
                // View Mode
                <>
                  <div className="p-4 flex items-center justify-between">
                    <CollapsibleTrigger asChild>
                      <button className="flex items-center gap-3 flex-1 text-left hover:opacity-80">
                        <Building2 className="h-5 w-5 text-primary" />
                        <div>
                          <h4 className="font-medium text-foreground">{outlet.name}</h4>
                          {outlet.address && (
                            <p className="text-sm text-muted-foreground">{outlet.address}</p>
                          )}
                        </div>
                        {expandedId === outlet.id ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground ml-auto" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto" />
                        )}
                      </button>
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-2 ml-4">
                      <Switch
                        checked={outlet.is_active}
                        onCheckedChange={() => handleToggleActive(outlet)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startEditing(outlet)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteOutlet(outlet.id, outlet.name)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 border-t border-border pt-4 space-y-4">
                      {(outlet.phone || outlet.email || outlet.hours) && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                          {outlet.phone && (
                            <div>
                              <span className="text-muted-foreground">Phone:</span>{" "}
                              <span className="text-foreground">{outlet.phone}</span>
                            </div>
                          )}
                          {outlet.email && (
                            <div>
                              <span className="text-muted-foreground">Email:</span>{" "}
                              <span className="text-foreground">{outlet.email}</span>
                            </div>
                          )}
                          {outlet.hours && (
                            <div>
                              <span className="text-muted-foreground">Hours:</span>{" "}
                              <span className="text-foreground">{outlet.hours}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="pt-4 border-t border-border">
                        <h5 className="font-medium text-foreground mb-4">Time Slots for {outlet.name}</h5>
                        <TimeSlotManager outletId={outlet.id} />
                      </div>
                    </div>
                  </CollapsibleContent>
                </>
              )}
            </div>
          </Collapsible>
        ))
      )}

      {/* Add New Outlet Form */}
      {showAddForm ? (
        <div className="p-4 rounded-lg bg-background border-2 border-primary/20 space-y-4">
          <h4 className="font-medium text-foreground">Add New Outlet</h4>
          
          <div className="space-y-2">
            <div>
            <Label htmlFor="selectOutlet">Outlet</Label>
            <Select value={newOutlet.name} onValueChange={(val) =>{ console.log(val,'valvalvalvalval'); setNewOutlet({ ...newOutlet, name: val })}}>
              <SelectTrigger id="selectOutlet" >
                <SelectValue placeholder="Select an outlet" />
              </SelectTrigger>
              <SelectContent>
                {outletList.map((outlet_) => {
                  const id = outlet_?.outletsupabaseReservationOutletID;

                  if (id === "" || id === null || id === undefined) {
                    return (
                      <SelectItem key={outlet_._id} value={outlet_._id}>
                        {outlet_.label}
                      </SelectItem>
                    );
                  }

                  return null;
                })}
              </SelectContent>
            </Select>
          </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={newOutlet.phone}
                onChange={(e) => setNewOutlet({ ...newOutlet, phone: e.target.value })}
                placeholder="+1 234 567 8900"
                className="bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newOutlet.email}
                onChange={(e) => setNewOutlet({ ...newOutlet, email: e.target.value })}
                placeholder="outlet@restaurant.com"
                className="bg-card"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <Textarea
              value={newOutlet.address}
              onChange={(e) => setNewOutlet({ ...newOutlet, address: e.target.value })}
              placeholder="123 Main Street, City"
              rows={2}
              className="bg-card resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label>Hours</Label>
            <Textarea
              value={newOutlet.hours}
              onChange={(e) => setNewOutlet({ ...newOutlet, hours: e.target.value })}
              placeholder="Monday-Sunday: 5:00 PM - 10:00 PM"
              rows={2}
              className="bg-card resize-none"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleAddOutlet} disabled={saving} className="flex-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Outlet
            </Button>
            <Button variant="outline" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        showAddOutlet && (
        <Button 
          variant="outline" 
          onClick={() => setShowAddForm(true)} 
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Outlet
        </Button>)
      )}
    </div>
  );
}
